import { FC, useEffect, useState } from 'react';

import { updateUserName } from '~/common/services/controllers/auth';
import { useToast } from '~/common/providers/toast';
import {
  filterNameChars,
  isValidFullName,
  normalizeFullName,
} from '~/common/utils/validate-full-name';
import { Typography } from '~/common/components/Typography';
import { Modal } from '~/common/components/Modal';
import { TextInput } from '~/common/components/Form/TextInput';
import { Button } from '~/common/components/Button';

import { StyFooter, StyWrapper } from './styles';

interface ICompleteNameModal {
  open: boolean;
  currentName?: string;
  onSaved: (name: string) => void;
}

// Modal obrigatório (sem botão de fechar e sem clique fora) exibido quando o nome
// cadastrado não é um nome completo válido. Corrige cadastros antigos e logins OAuth.
export const CompleteNameModal: FC<ICompleteNameModal> = ({
  open,
  currentName,
  onSaved,
}) => {
  const { showToast } = useToast();
  const [name, setName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setName(filterNameChars(currentName || ''));
      setError('');
    }
  }, [open, currentName]);

  const handleSave = async (): Promise<void> => {
    const normalized = normalizeFullName(name);

    if (!isValidFullName(normalized)) {
      setError('Insira seu nome completo (nome e sobrenome)');
      return;
    }

    setSaving(true);
    try {
      await updateUserName(normalized);
      onSaved(normalized);
    } catch (requestError) {
      showToast({
        type: 'error',
        position: 'top-right',
        title: 'Não foi possível salvar',
        description:
          (requestError as any)?.data?.message ||
          'Ocorreu um erro ao salvar seu nome, contate nosso suporte no e-mail contact@cryptocontrol.pro',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      id="complete-name-modal"
      isOpen={open}
      onClose={() => undefined}
      hasCloseButton={false}
      justifyContent="start"
      height="auto"
      width="400px"
      title="Confirme seu nome completo"
      footer={
        <StyFooter>
          <Button
            text={saving ? 'Salvando...' : 'Salvar'}
            width="100%"
            justifyContent="center"
            disabled={saving}
            backgroundColor="#1FC16B1A"
            textOrIconColor="#20CD74"
            onClick={handleSave}
          />
        </StyFooter>
      }
    >
      <StyWrapper>
        <Typography.Label size="x-small" color="neutral.500">
          Precisamos do seu nome e sobrenome para manter seu cadastro em dia.
        </Typography.Label>

        <TextInput
          type="text"
          name="complete-user-name"
          autoComplete="name"
          size="large"
          width="100%"
          style={{ width: '100%' }}
          label={{
            label: 'Nome completo',
            isRequired: true,
          }}
          value={name}
          error={error}
          minLength={3}
          maxLength={50}
          onChange={(e) => {
            setName(filterNameChars(e.target.value));
            setError('');
          }}
          placeholder="Ex.: George Lucas"
        />
      </StyWrapper>
    </Modal>
  );
};
